import type { ThemesClient } from "../services/themes/themes-client.ts";

/**
 * The host environment's delegate (the page, a script, a CLI, a server):
 * layout queries, the persistence-flush lifecycle and the theme backend.
 *
 * Injected into `LonaSdk.Client` (`ClientConfig.host`) so SDK internals
 * can notify the host environment
 * of changes without depending on UI code.
 *
 * Row-level mutation invalidation is handled by InvalidationSink — this
 * interface only carries notifications that aren't rendering events.
 */
export interface SheetHost {
  // ── Infrastructure ──
  //
  // Which executor runs a client's durable writes is the client's
  // `debounceExecutor` alone (`DebounceExecutors.browser(bridge)` installs
  // the page's), never the platform.

  /**
   * Register a synchronous flush hook for lifecycle boundaries where page-local
   * debounce timers would otherwise be lost before they reach the durable API
   * debounce queue. Returns the uninstall: the host drops `flush` and never
   * calls it again (a disposed client calls it, so the page holds no hook of
   * a client it no longer has).
   */
  installPersistenceFlush(flush: () => void): () => void;

  // ── Layout ──

  /** Returns the maximum available height for the sheet viewport. */
  getMaxHeight(): number;
  /** Returns the maximum sidebar width (typically window width minus sheet minimum). */
  getMaxWidth?(): number;

  // ── Theme ──

  /** Apply theme visuals (CSS injection, etc.). */
  themeBackend?: ThemesClient.Backend;
}
/** Values that go with `SheetHost`: `HEADLESS`. */
export namespace SheetHost {
  /**
   * The delegate of a host with no page: a script, a CLI, a server, a test.
   * A fixed viewport (800 x 1820), no persistence-flush lifecycle, no theme
   * backend. Shared by every headless client in the process, so frozen: a
   * host that needs another delegate spreads it (`{ ...SheetHost.HEADLESS,
   * getMaxHeight: () => 600 }`).
   */
  export const HEADLESS: SheetHost = Object.freeze<SheetHost>({
    installPersistenceFlush(_flush) {
      return () => {};
    },
    getMaxHeight: () => 800,
    getMaxWidth: () => 1820,
  });
}
